import { useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom-v5-compat";
import type {
  BookedFlightItineraryWithDepartureTime,
  DisruptionProtectionDisruptionProtectionPolicyDetails,
} from "@b2bportal/air-booking-api";
import type { PaymentOpaqueValue } from "@b2bportal/air-disruption-api";
import type { FiatPrice } from "@b2bportal/air-shopping-api";
import { useDisruptionStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  type AppDispatch,
  CoreDisruptionComponent,
} from "@b2bportal/core-types";
import { useDeviceType } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  DisruptionExerciseFooter,
  DisruptionExerciseOptionsCard,
  DisruptionExercisePage,
  DisruptionExercisePageWidth,
  DisruptionExercisePageContent,
  DisruptionExercisePolicyDetails,
  DisruptionExerciseSummaryCard,
  getDisruptionExerciseProgress,
  DisruptionExerciseProgress,
} from "@tenant/disruption";

import defaultStyles from "./DisruptionExerciseLandingPage.module.scss";

export interface DisruptionExerciseLandingPageProps {
  disruptionProductName: string;
  disruptionProductLogo: string;
  disruptedFlight: BookedFlightItineraryWithDepartureTime;
  contractId: string;
  setReimbursementTotalAmount: (amount: FiatPrice) => void;
  setRefundBreakdown: (breakdown: Array<PaymentOpaqueValue>) => void;
  setRefundFulfillmentToken: (token: string) => void;
  policyDetails: DisruptionProtectionDisruptionProtectionPolicyDetails;
  onContactSupport: () => void;
  termsLink: string;
  displayExternalLinkAsModal: boolean;
  delayHours: string;
  hoursString: string;
  rebookingLimit?: string;
  disruptionSelfServeRebook: boolean;
  cancelFlowAction?: () => void;
}

export const DisruptionExerciseLandingPage = ({
  disruptionProductName,
  disruptionProductLogo,
  policyDetails,
  onContactSupport,
  termsLink,
  displayExternalLinkAsModal,
  delayHours,
  hoursString,
  rebookingLimit,
  disruptedFlight,
  cancelFlowAction,
}: DisruptionExerciseLandingPageProps) => {
  const { t } = useI18nContext();
  const { isDesktopAndUp } = useDeviceType();
  const COMPONENT_KEY = CoreDisruptionComponent.DisruptionExerciseLandingPage;
  const styles = useDisruptionStyles(COMPONENT_KEY, defaultStyles);
  const [_, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const dispatch = useDispatch<AppDispatch>();
  const disruptionExerciseProgress = useSelector(getDisruptionExerciseProgress);
  const [refundPlanError, setRefundPlanError] = useState<string | null>(null);
  const [policyDetailsExpanded, setPolicyDetailsExpanded] = useState(true);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const shouldEnableCta =
    disruptionExerciseProgress === DisruptionExerciseProgress.RebookSelected ||
    disruptionExerciseProgress === DisruptionExerciseProgress.RefundSelected;

  const handleCta = useCallback(() => {
    //Temporarily redirect to contact support modal for both rebook and refund
    //TODO: Revert this change once we are ready to launch self-serve rebooking for Uber
    if (
      disruptionExerciseProgress === DisruptionExerciseProgress.RebookSelected
    ) {
      onContactSupport();
    } else if (
      disruptionExerciseProgress === DisruptionExerciseProgress.RefundSelected
    ) {
      onContactSupport();
    }
  }, [disruptionExerciseProgress, onContactSupport]);

  const onExpand = useCallback(() => {
    setPolicyDetailsExpanded(!policyDetailsExpanded);
  }, [setPolicyDetailsExpanded, policyDetailsExpanded]);

  const handleGoBack = () => {
    navigate(`/trips?tripId=${disruptedFlight.bookedItinerary.id}`);
  };

  const policyDetailsContent = (
    <DisruptionExercisePolicyDetails
      policyDetails={policyDetails}
      expanded={policyDetailsExpanded}
      onExpand={onExpand}
      termsLink={termsLink}
      displayExternalLinkAsModal={displayExternalLinkAsModal}
    ></DisruptionExercisePolicyDetails>
  );

  return (
    <DisruptionExercisePage
      className={cn("DisruptionExerciseLandingPage")}
      pageWidth={DisruptionExercisePageWidth.Large}
      title={disruptionProductName}
      disruptionProductLogo={disruptionProductLogo}
      onBack={handleGoBack}
      openErrorModal={refundPlanError !== null}
      onCloseErrorModal={() => {
        setRefundPlanError(null);
      }}
      onSupportClick={onContactSupport}
      submitButtonTitle={t(
        "core-disruption.disruptionUniversalExercise.continue"
      )}
      submitButtonAction={
        disruptionExerciseProgress === DisruptionExerciseProgress.LandingPage
          ? undefined
          : handleCta
      }
      submitButtonDisabled={!shouldEnableCta}
      isLoading={loading}
      termsLink={termsLink}
      onCancelFlow={cancelFlowAction}
      content={
        <DisruptionExercisePageContent
          leftContent={[
            <DisruptionExerciseSummaryCard
              title={t(
                "core-disruption.disruptionUniversalExercise.landingPage.title"
              )}
              description={t(
                "core-disruption.disruptionUniversalExercise.landingPage.subtitleWithRebookingLimit",
                {
                  rebookLimit: rebookingLimit,
                }
              )}
              key={"landing-page-header-content"}
            ></DisruptionExerciseSummaryCard>,
            <DisruptionExerciseOptionsCard
              onSupportClick={onContactSupport}
              key={"landing-page-options"}
            ></DisruptionExerciseOptionsCard>,
          ]}
          rightConent={[
            policyDetailsContent,
            isDesktopAndUp ? (
              <div></div>
            ) : (
              <DisruptionExerciseFooter
                onSupportClick={onContactSupport}
              ></DisruptionExerciseFooter>
            ),
          ]}
        />
      }
      delayHours={delayHours}
      numberOfHours={hoursString}
    ></DisruptionExercisePage>
  );
};

export default DisruptionExerciseLandingPage;
